import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../../store/admin/vehiclesSocketStore";
import {
  useDeviceEventsStore,
  getEventLabel,
  type SocketEvent,
} from "../../../../../../store/admin/deviceEventsStore";

// Helper para mostrar el label del evento (si es código 43, muestra event_type)
const getEventDisplayLabel = (event: SocketEvent): string => {
  if (event.event_code === 43 && event.event_type) {
    return event.event_type;
  }
  return getEventLabel(event.event_code);
};
import CreateEventModal from "./modals/CreateEventModal";
import EventsHistoryModal from "./modals/EventHistoryMidal";

interface EventsContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

type ModalType = "create" | "history" | null;

export default function EventsContent({
  vehicle,
  isDarkMode,
}: EventsContentProps) {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const { t } = useTranslation("lite");

  // Obtener el objeto de eventos por device
  const eventsByDevice = useDeviceEventsStore((state) => state.eventsByDevice);

  // Memorizar los eventos del device actual para evitar loop infinito
  const socketEvents = useMemo<SocketEvent[]>(() => {
    if (!vehicle?.id) return [];
    return eventsByDevice[vehicle.id] || [];
  }, [eventsByDevice, vehicle?.id]);

  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  // Formatear timestamp a fecha/hora local de la máquina
  const formatSocketEventTime = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatSocketEventDate = (timestamp: number | null): string => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleOpenCreateFromHistory = () => {
    setActiveModal("create");
  };

  return (
    <>
      <div className={`p-3 border-t ${borderColor}`}>
        {/* Header de la tabla */}
        <div
          className={`grid grid-cols-12 gap-2 text-[10px] font-medium ${textSecondary} mb-2 pb-2 border-b ${borderColor}`}
        >
          <div className="col-span-4">{t("eventsContent.events")}</div>
          <div className="col-span-4">{t("eventsContent.dateTime")}</div>
          <div className="col-span-4">{t("eventsContent.code")}</div>
        </div>

        {/* Empty state - solo si no hay eventos del socket */}
        {socketEvents.length === 0 && (
          <div className="text-center py-4">
            <p className={`text-xs ${textSecondary}`}>
              {t("eventsContent.waitingEvents")}
            </p>
          </div>
        )}

        {/* Lista de eventos del socket (solo 3) */}
        {socketEvents.length > 0 && (
          <div className="space-y-2 mb-4">
            {socketEvents.slice(0, 3).map((socketEvent) => (
              <div
                key={socketEvent.id}
                className={`grid grid-cols-12 gap-2 text-[10px] items-center py-2 border-b ${borderColor} last:border-b-0`}
              >
                <div className={`col-span-4 ${textColor} font-medium truncate`}>
                  {getEventDisplayLabel(socketEvent)}
                </div>
                <div className={`col-span-4 ${textSecondary}`}>
                  <div>{formatSocketEventDate(socketEvent.timeStamp)}</div>
                  <div>{formatSocketEventTime(socketEvent.timeStamp)}</div>
                </div>
                <div className={`col-span-4 ${textSecondary}`}>
                  {socketEvent.event_code ? `#${socketEvent.event_code}` : "-"}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Botón Crear evento */}
        <button
          onClick={() => setActiveModal("create")}
          className="w-full py-2 rounded-lg bg-primary-500 text-black text-xs font-medium hover:bg-primary-400 transition-colors mb-2"
        >
          {t("eventsContent.createEvent", { defaultValue: "Crear evento" })}
        </button>

        {/* Botón Ver más */}
        <button
          onClick={() => setActiveModal("history")}
          className={`w-full py-2 rounded-lg border ${borderColor} ${textColor} text-xs font-medium hover:border-primary-500 hover:text-primary-500 transition-colors flex items-center justify-center gap-1`}
        >
          {t("eventsContent.viewMore")} <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      {/* Modales */}
      {activeModal === "create" && (
        <CreateEventModal
          vehicle={vehicle}
          isDarkMode={isDarkMode}
          onClose={handleCloseModal}
        />
      )}

      {activeModal === "history" && (
        <EventsHistoryModal
          vehicle={vehicle}
          isDarkMode={isDarkMode}
          onClose={handleCloseModal}
          onCreateEvent={handleOpenCreateFromHistory}
        />
      )}
    </>
  );
}
